import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, ScrollView, TouchableOpacity, Alert, RefreshControl, ActivityIndicator } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import tw from 'twrnc';

import { isScreenshotMode, DEMO_USER_XP, DEMO_USER_STREAK, DEMO_ACHIEVEMENTS } from '../../../../lib/screenshotMode';
import { useAppTheme } from '../../../design-system/ThemeProvider';
import { t } from '../../../i18n/translations';
import {
  getUserGamificationProfile,
  getDailyVerse,
  completeDailyVerse,
  hasCompletedDailyVerse,
  subscribeToGamificationUpdates,
  getUserXPTransactions,
  getUserAchievements,
  formatXP,
  type GamificationProfile,
  type DailyVerse,
  type XPTransaction,
  type UserAchievement
} from '../../../shared/services/gamificationService';
import { iqesColors } from '../../../shared/ui/iqesDesignSystem';
import { useLanguageStore } from '../../quiz-core/languageStore';
import { useUserProfile } from '../../quiz-core/useStore';
import { achievementText } from '../../../shared/services/achievementText';

const shellColors = {
  canvas: iqesColors.canvas,
  panel: 'rgba(8,10,24,0.94)',
  panelRaised: 'rgba(16,12,34,0.96)',
  border: 'rgba(246,190,58,0.34)',
  borderStrong: 'rgba(246,190,58,0.52)',
  gold: iqesColors.gold,
  goldBright: iqesColors.goldBright,
  goldSoft: iqesColors.goldSoft,
  ivory: iqesColors.ivory,
  muted: 'rgba(255,248,232,0.72)',
};

interface GamificationDashboardProps {
  onNavigateToAchievements?: () => void;
  onNavigateToStreakCalendar?: () => void;
  onNavigateToLeaderboard?: () => void;
}

type DashboardProfile = GamificationProfile & {
  achievement_count?: number;
  global_rank?: number;
};

type DashboardVerse = DailyVerse & {
  content?: string;
  reference?: string;
  verse_reference?: string;
  verse_text?: string;
};

interface QuickDestinationCardProps {
  title: string;
  subtitle: string;
  icon: string;
  iconBackground: string;
  borderColor: string;
  onPress: () => void;
  colors: ReturnType<typeof useAppTheme>['colors'];
}

const QuickDestinationCard: React.FC<QuickDestinationCardProps> = ({
  title,
  subtitle,
  icon,
  iconBackground,
  borderColor,
  onPress,
  colors,
}) => (
  <TouchableOpacity
    activeOpacity={0.92}
    onPress={onPress}
    style={{
      backgroundColor: shellColors.panel,
      borderRadius: 18,
      paddingHorizontal: 16,
      paddingVertical: 16,
      flexDirection: 'row',
      alignItems: 'center',
      borderWidth: 1,
      borderColor: borderColor || shellColors.border,
      shadowColor: colors.black,
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.04,
      shadowRadius: 8,
      elevation: 4,
      minHeight: 96,
    }}
  >
    <View
      style={{
        backgroundColor: iconBackground,
        borderRadius: 16,
        width: 48,
        height: 48,
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 14,
      }}
      pointerEvents="none"
    >
      <Text style={{ fontSize: 24, color: 'white' }}>{icon}</Text>
    </View>

    <View style={{ flex: 1 }} pointerEvents="none">
      <Text style={{ fontWeight: '700', color: shellColors.ivory, fontSize: 16, marginBottom: 4 }}>
        {title}
      </Text>
      <Text style={{ fontSize: 12, color: shellColors.muted }}>
        {subtitle}
      </Text>
    </View>

    <TouchableOpacity
      onPress={onPress}
      hitSlop={{ top: 12, right: 12, bottom: 12, left: 12 }}
      style={{
        width: 36,
        height: 36,
        borderRadius: 18,
        alignItems: 'center',
        justifyContent: 'center',
      }}
      accessibilityRole="button"
    >
      <Ionicons name="chevron-forward" size={18} color={shellColors.gold} />
    </TouchableOpacity>
  </TouchableOpacity>
);

export const GamificationDashboard: React.FC<GamificationDashboardProps> = ({
  onNavigateToAchievements,
  onNavigateToStreakCalendar,
  onNavigateToLeaderboard
}) => {
  const navigation = useNavigation();
  const { id: userId, name: userName, loading: userLoading } = useUserProfile();
  const { language } = useLanguageStore();
  const { colors } = useAppTheme();
  const screenshotMode = isScreenshotMode();

  // Build demo profile from screenshot data
  const demoProfile: DashboardProfile | null = screenshotMode ? {
    total_xp: DEMO_USER_XP.total_xp,
    current_level: DEMO_USER_XP.current_level,
    xp_to_next_level: DEMO_USER_XP.xp_to_next_level,
    rank: DEMO_USER_XP.rank,
    current_streak: DEMO_USER_STREAK.current_streak,
    achievement_count: DEMO_ACHIEVEMENTS.length,
    global_rank: 6,
  } : null;

  const [profile, setProfile] = useState<DashboardProfile | null>(screenshotMode ? demoProfile : null);
  const [dailyVerse, setDailyVerse] = useState<DailyVerse | null>(null);
  const [verseCompleted, setVerseCompleted] = useState(false);
  const [recentTransactions, setRecentTransactions] = useState<XPTransaction[]>([]);
  const [recentAchievements, setRecentAchievements] = useState<UserAchievement[]>([]);
  const [loading, setLoading] = useState(screenshotMode ? false : true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadGamificationData = useCallback(async (isRefresh = false) => {
    if (!userId) {
      console.log('[GamificationDashboard] No user ID, skipping data load');
      return;
    }

    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      // Load all data in parallel for better performance
      const [profileData, verse, transactions, achievements] = await Promise.all([
        getUserGamificationProfile(userId) as Promise<DashboardProfile>,
        getDailyVerse(language),
        getUserXPTransactions(userId, 5), // Get last 5 transactions
        getUserAchievements(userId)
      ]);
      setProfile(profileData);
      setDailyVerse(verse);
      setRecentTransactions(transactions);

      // Filter for recently completed achievements (last 7 days)
      const recentlyCompleted = achievements.filter(ach => {
        if (!ach.completed || !ach.completed_at) return false;
        const completedDate = new Date(ach.completed_at);
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        return completedDate > weekAgo;
      }).slice(0, 3); // Show max 3 recent achievements

      setRecentAchievements(recentlyCompleted);

      // Check if daily verse is completed
      if (verse) {
        const completed = await hasCompletedDailyVerse(userId);
        setVerseCompleted(completed);
      }
    } catch (error) {
      console.error('[GamificationDashboard] Error loading gamification data:', error);
      setError(error instanceof Error ? error.message : 'Failed to load gamification data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [userId, language]);

  const handleCompleteDailyVerse = useCallback(async () => {
    if (!userId || !dailyVerse || verseCompleted) return;

    try {
      console.log('[GamificationDashboard] Completing daily verse...');
      const success = await completeDailyVerse(userId);

      if (success) {
        // Update only the local state - no need to reload all data
        setVerseCompleted(true);

        // Optionally update XP locally without full reload
        if (profile) {
          setProfile({
            ...profile,
            total_xp: profile.total_xp + 5 // Award 5 XP for daily verse
          });
        }

        Alert.alert(
          t('gamification.congratulations', language) || 'Congratulations!',
          t('gamification.daily_verse_completed_message', language) || 'Daily verse completed! You earned 5 XP.',
          [{ text: t('ok', language) || 'OK' }]
        );
      }
    } catch (error) {
      console.error('[GamificationDashboard] Error completing daily verse:', error);
      Alert.alert(
        t('error', language) || 'Error',
        t('failed_to_complete_daily_verse', language) || 'Failed to complete daily verse. Please try again.'
      );
    }
  }, [userId, dailyVerse, verseCompleted, profile, language]);

  const getXPProgressPercentage = useCallback(() => {
    if (!profile || !profile.xp_to_next_level) return 0;
    const currentLevelXP = profile.total_xp - (profile.xp_to_next_level || 0);
    const nextLevelXP = profile.xp_to_next_level;
    return Math.min(100, Math.max(0, (currentLevelXP / nextLevelXP) * 100));
  }, [profile]);

  const getTranslatedRank = useCallback((rank: string) => {
    const rankKey = rank?.toLowerCase();
    if (rankKey && ['beginner', 'scholar', 'teacher', 'sage', 'intermediate', 'advanced', 'expert', 'master'].includes(rankKey)) {
      return t(`gamification.${rankKey}`, language);
    }
    return rank; // fallback to original rank if not found
  }, [language]);

  const getTranslatedDescription = useCallback((description: string) => {
    if (!description) return description;

    // Match "Quiz completed with X% accuracy" pattern
    const accuracyMatch = description.match(/Quiz completed with (\d+)% accuracy/i);
    if (accuracyMatch) {
      const score = accuracyMatch[1];
      return t('gamification.quiz_completed_with_score', language).replace('{score}', score);
    }

    // Match multiplayer "Rank X/Y with Z% accuracy" pattern
    const multiplayerAccuracyMatch = description.match(/Multiplayer quiz completed - Rank (.+?) with (\d+)% accuracy/i);
    if (multiplayerAccuracyMatch) {
      const score = multiplayerAccuracyMatch[2];
      return t('gamification.quiz_completed_with_score', language).replace('{score}', score);
    }

    // Match "Quiz completed with X% score" pattern
    const scoreMatch = description.match(/Quiz completed with (\d+)% score/i);
    if (scoreMatch) {
      const score = scoreMatch[1];
      return t('gamification.quiz_completed_with_score', language).replace('{score}', score);
    }

    // Match "Quiz completed" pattern
    if (description.match(/Quiz completed/i)) {
      return t('gamification.quiz_completed', language);
    }

    // Match "Daily challenge completed" pattern
    if (description.match(/Daily challenge completed/i)) {
      return t('gamification.daily_challenge_completed', language);
    }

    return description; // fallback to original description
  }, [language]);

  const getStreakEmoji = useCallback((streak: number) => {
    if (streak >= 30) return '🔥';
    if (streak >= 14) return '⚡';
    if (streak >= 7) return '✨';
    if (streak >= 3) return '💫';
    return '📅';
  }, []);

  const formatDayLabel = useCallback((count: number) => {
    return count === 1
      ? t('gamification.day_singular', language)
      : t('gamification.days', language);
  }, [language]);

  const handleAchievementsNavigation = useCallback(() => {
    if (onNavigateToAchievements) {
      onNavigateToAchievements();
      return;
    }

    navigation.navigate('Achievements' as never);
  }, [navigation, onNavigateToAchievements]);

  const getRankDisplay = useCallback((rank?: number | null) => {
    if (!rank || rank <= 0) {
      return t('gamification.unranked', language);
    }

    return `#${rank}`;
  }, [language]);

  const handleStreakNavigation = useCallback(() => {
    if (onNavigateToStreakCalendar) {
      onNavigateToStreakCalendar();
      return;
    }

    navigation.navigate('StreakCalendar' as never);
  }, [navigation, onNavigateToStreakCalendar]);

  const handleLeaderboardNavigation = useCallback(() => {
    if (onNavigateToLeaderboard) {
      onNavigateToLeaderboard();
      return;
    }

    navigation.navigate('XPLeaderboard' as never);
  }, [navigation, onNavigateToLeaderboard]);

  // Screenshot mode: signal readiness after first render
  useEffect(() => {
    if (screenshotMode && typeof window !== 'undefined') {
      (window as typeof window & { __screenshotReady?: string }).__screenshotReady = '05_dashboard';
    }
  }, [screenshotMode]);

  // Set up real-time subscriptions
  useEffect(() => {
    if (screenshotMode) return;
    if (!userId) return;

    const subscription = subscribeToGamificationUpdates(userId, {
      onXPUpdate: () => {
        loadGamificationData();
      },
      onStreakUpdate: () => {
        loadGamificationData();
      },
      onAchievementUpdate: () => {
        loadGamificationData();
      }
    });

    return () => {
      if (subscription && subscription.unsubscribe) {
        subscription.unsubscribe();
      }
    };
  }, [userId, loadGamificationData]);

  // Initial data load
  useEffect(() => {
    if (screenshotMode) return;
    if (userId && !userLoading) {
      loadGamificationData();
    }
  }, [userId, userLoading, loadGamificationData, screenshotMode]);

  if (loading && !refreshing) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: shellColors.canvas }}>
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', paddingHorizontal: 24 }}>
          <View style={{
            backgroundColor: shellColors.panel,
            borderRadius: 24,
            padding: 32,
            alignItems: 'center',
            shadowColor: colors.black,
            shadowOffset: { width: 0, height: 8 },
            shadowOpacity: 0.08,
            shadowRadius: 16,
            elevation: 12,
          }}>
            <ActivityIndicator size="large" color={shellColors.gold} />
            <Text style={{ marginTop: 16, color: shellColors.ivory, fontSize: 18, fontWeight: '600' }}>
              {t('gamification.loading_progress', language)}...
            </Text>
          </View>
        </View>
      </SafeAreaView>
    );
  }

  if (error) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: shellColors.canvas }}>
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', paddingHorizontal: 24 }}>
          <View style={{
            backgroundColor: shellColors.panel,
            borderRadius: 24,
            padding: 32,
            alignItems: 'center',
            shadowColor: colors.black,
            shadowOffset: { width: 0, height: 8 },
            shadowOpacity: 0.08,
            shadowRadius: 16,
            elevation: 12,
          }}>
            <Text style={{ fontSize: 48, marginBottom: 16 }}>⚠️</Text>
            <Text style={{ color: shellColors.ivory, textAlign: 'center', fontSize: 20, fontWeight: '700', marginBottom: 16 }}>
              {t('gamification.error_loading_progress', language)}
            </Text>
            <Text style={{ color: shellColors.muted, textAlign: 'center', marginBottom: 24, fontSize: 16 }}>
              {error}
            </Text>
            <TouchableOpacity
              style={{
                backgroundColor: shellColors.goldSoft,
                paddingHorizontal: 24,
                paddingVertical: 12,
                borderRadius: 12,
                shadowColor: shellColors.gold,
                shadowOffset: { width: 0, height: 4 },
                shadowOpacity: 0.3,
                shadowRadius: 8,
                elevation: 6,
              }}
              onPress={() => loadGamificationData()}
            >
              <Text style={{ color: iqesColors.navy, fontWeight: '800', fontSize: 16 }}>
                {t('gamification.try_again', language)}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: shellColors.canvas }}>
      <ScrollView
        style={{ flex: 1, paddingHorizontal: 24 }}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={() => loadGamificationData(true)}
            colors={[shellColors.gold]}
            tintColor={shellColors.gold}
          />
        }
      >
          {/* Progress Hero */}
          <View style={{
            borderRadius: 24,
            padding: 28,
            marginBottom: 20,
            marginTop: 20,
            backgroundColor: shellColors.panel,
            borderWidth: 1,
            borderColor: shellColors.border,
            shadowColor: colors.black,
            shadowOffset: { width: 0, height: 6 },
            shadowOpacity: 0.06,
            shadowRadius: 12,
            elevation: 8,
          }}>
            <View style={tw`flex-row items-center justify-between`}>
              <View style={{ flex: 1, paddingRight: 16 }}>
                <Text style={{
                  fontSize: 12,
                  fontWeight: '700',
                  color: shellColors.goldSoft,
                  marginBottom: 8,
                  letterSpacing: 1,
                  textTransform: 'uppercase',
                }}>
                  {t('gamification.your_progress', language)}
                </Text>
                <Text style={{
                  fontSize: 28,
                  fontWeight: '800',
                  color: shellColors.ivory,
                  marginBottom: 8,
                  letterSpacing: 0.2
                }}>
                  {screenshotMode ? t('coreUI.player', language) : (userName || t('profile.title', language))}
                </Text>
                <Text style={{
                  fontSize: 15,
                  color: shellColors.muted,
                  lineHeight: 22,
                  fontWeight: '500'
                }}>
                  {t('gamification.dashboard_subtitle', language)}
                </Text>
              </View>

              <View style={{
                borderRadius: 20,
                padding: 18,
                backgroundColor: 'rgba(246,190,58,0.16)',
                shadowColor: shellColors.gold,
                shadowOffset: { width: 0, height: 6 },
                shadowOpacity: 0.25,
                shadowRadius: 10,
                elevation: 8,
              }}>
                <Text style={{ fontSize: 34, color: 'white' }}>✦</Text>
              </View>
            </View>
          </View>

          {/* Premium XP Progress Card */}
          {profile && (
            <View style={[
              tw`rounded-2xl p-6 mb-6`,
              {
                backgroundColor: shellColors.panel,
                borderWidth: 1,
                borderColor: shellColors.border,
                shadowColor: colors.black,
                shadowOffset: { width: 0, height: 6 },
                shadowOpacity: 0.06,
                shadowRadius: 12,
                elevation: 8,
              }
            ]}>
              <View style={tw`flex-row items-center justify-between mb-4`}>
                <View>
                  <Text style={[tw`text-2xl font-bold`, { color: shellColors.ivory }]}>
                    {t('gamification.level', language)} {profile.current_level}
                  </Text>
                  <Text style={[tw`text-lg font-semibold`, { color: shellColors.muted }]}>
                    {getTranslatedRank(profile.rank)}
                  </Text>
                </View>
                <View style={tw`items-end`}>
                  <Text style={[tw`text-3xl font-bold`, { color: shellColors.ivory }]}>
                    {formatXP(profile.total_xp)}
                  </Text>
                  <Text style={{ color: shellColors.muted }}>{t('gamification.xp_short', language)}</Text>
                </View>
              </View>

              {/* XP Progress Bar */}
              <View style={tw`mb-4`}>
                <View style={tw`flex-row justify-between mb-2`}>
                  <Text style={[tw`text-sm`, { color: shellColors.muted }]}>
                    {t('gamification.progress_to_next_level', language)}
                  </Text>
                  <Text style={[tw`text-sm font-semibold`, { color: shellColors.ivory }]}>
                    {getXPProgressPercentage()}%
                  </Text>
                </View>
                <View style={[tw`h-3 rounded-full overflow-hidden`, { backgroundColor: 'rgba(255,248,232,0.12)' }]}>
                  <View
                    style={[tw`h-full rounded-full`, { width: `${getXPProgressPercentage()}%`, backgroundColor: shellColors.goldSoft }]}
                  />
                </View>
              </View>

              {/* Stats Row */}
              <View style={[tw`flex-row justify-around pt-4`, { borderTopWidth: 1, borderTopColor: shellColors.border }]}>
                <View style={tw`items-center`}>
                  <Text style={[tw`text-2xl font-bold`, { color: shellColors.ivory }]}>
                    {profile.current_streak}
                  </Text>
                  <Text style={[tw`text-sm`, { color: shellColors.muted }]}>
                    {getStreakEmoji(profile.current_streak)} {t('gamification.day_streak', language)}
                  </Text>
                </View>
                <View style={tw`items-center`}>
                  <Text style={[tw`text-2xl font-bold`, { color: shellColors.ivory }]}>
                    {getRankDisplay(profile.global_rank)}
                  </Text>
                  <Text style={[tw`text-sm`, { color: shellColors.muted }]}>
                    {t('gamification.global_rank', language)}
                  </Text>
                </View>
                <View style={tw`items-center`}>
                  <Text style={[tw`text-2xl font-bold`, { color: shellColors.ivory }]}>
                    {profile.achievement_count || 0}
                  </Text>
                  <Text style={[tw`text-sm`, { color: shellColors.muted }]}>
                    🏆 {t('gamification.achievements', language)}
                  </Text>
                </View>
              </View>
            </View>
          )}

          {/* Premium Daily Verse Card */}
          {dailyVerse && (
            <View style={[
              tw`rounded-2xl p-6 mb-6`,
              {
                backgroundColor: shellColors.panel,
                borderWidth: 1,
                borderColor: shellColors.border,
                shadowColor: colors.black,
                shadowOffset: { width: 0, height: 6 },
                shadowOpacity: 0.06,
                shadowRadius: 12,
                elevation: 8,
              }
            ]}>
              <View style={tw`flex-row items-center justify-between mb-4`}>
                <Text style={[tw`text-xl font-bold`, { color: shellColors.ivory }]}>
                  📖 {t('gamification.daily_verse', language)}
                </Text>
                {verseCompleted && (
                  <View style={{
                    backgroundColor: '#10B981',
                    paddingHorizontal: 12,
                    paddingVertical: 4,
                    borderRadius: 20,
                  }}>
                    <Text style={[tw`font-semibold text-sm`, { color: 'white' }]}>
                      ✓ {t('completed', language)}
                    </Text>
                  </View>
                )}
              </View>

              <Text style={[tw`text-base leading-6 mb-4`, { color: shellColors.muted }]}>
                "{(dailyVerse as DashboardVerse).verse_text || (dailyVerse as DashboardVerse).content}"
              </Text>

              <Text style={[tw`font-semibold mb-4`, { color: shellColors.muted }]}>
                {(dailyVerse as DashboardVerse).verse_reference || (dailyVerse as DashboardVerse).reference}
              </Text>

              {!verseCompleted && (
                <TouchableOpacity
                  style={{
                    backgroundColor: shellColors.goldSoft,
                    paddingVertical: 12,
                    paddingHorizontal: 24,
                    borderRadius: 12,
                    shadowColor: shellColors.gold,
                    shadowOffset: { width: 0, height: 4 },
                    shadowOpacity: 0.3,
                    shadowRadius: 8,
                    elevation: 6,
                  }}
                  onPress={handleCompleteDailyVerse}
                >
                  <Text style={[tw`font-semibold text-center`, { color: iqesColors.navy }]}>
                    {t('gamification.mark_as_read', language)} (+5 {t('gamification.xp_short', language)})
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          )}

          {/* Quick Destinations */}
          <View style={[
            tw`rounded-2xl p-6 mb-6`,
            {
              backgroundColor: shellColors.panel,
              borderWidth: 1,
              borderColor: shellColors.border,
              shadowColor: colors.black,
              shadowOffset: { width: 0, height: 6 },
              shadowOpacity: 0.06,
              shadowRadius: 12,
              elevation: 8,
            }
          ]}>
            <Text style={[tw`text-xs font-bold mb-2`, { color: shellColors.goldSoft, letterSpacing: 1, textTransform: 'uppercase' }]}>
              {t('gamification.quick_actions', language)}
            </Text>
            <Text style={[tw`text-xl font-bold mb-4`, { color: shellColors.ivory }]}>
              {t('settings.title', language)}
            </Text>

            <View>
              <QuickDestinationCard
                title={t('gamification.achievements', language)}
                subtitle={`${profile?.achievement_count || 0} ${t('gamification.unlocked', language)}`}
                icon="🏆"
                iconBackground="#F59E0B"
                  borderColor={shellColors.border}
                onPress={handleAchievementsNavigation}
                colors={colors}
              />

              <View style={{ height: 12 }} />

              <QuickDestinationCard
                title={t('gamification.streak_calendar', language)}
                subtitle={`${profile?.current_streak || 0} ${formatDayLabel(profile?.current_streak || 0)}`}
                icon="📅"
                iconBackground="#10B981"
                  borderColor={shellColors.border}
                onPress={handleStreakNavigation}
                colors={colors}
              />

              <View style={{ height: 12 }} />

              <QuickDestinationCard
                title={t('gamification.leaderboard', language)}
                subtitle={profile?.global_rank
                  ? `#${profile.global_rank} ${t('gamification.global_rank', language)}`
                  : t('gamification.unranked', language)}
                icon="🏅"
                iconBackground="#EF4444"
                  borderColor={shellColors.border}
                onPress={handleLeaderboardNavigation}
                colors={colors}
              />
            </View>
          </View>

          {/* Premium Recent Activity Card */}
          {(recentTransactions.length > 0 || recentAchievements.length > 0) && (
            <View style={[
              tw`rounded-2xl p-6 mb-6`,
              {
                backgroundColor: shellColors.panel,
                borderWidth: 1,
                borderColor: shellColors.border,
                shadowColor: colors.black,
                shadowOffset: { width: 0, height: 6 },
                shadowOpacity: 0.06,
                shadowRadius: 12,
                elevation: 8,
              }
            ]}>
              <Text style={[tw`text-xl font-bold mb-4`, { color: shellColors.ivory }]}>
                🎯 {t('gamification.recent_activity', language)}
              </Text>

              {/* Recent XP Transactions */}
              {recentTransactions.length > 0 && (
                <View style={tw`mb-4`}>
                  <Text style={[tw`text-sm font-semibold mb-3`, { color: shellColors.muted }]}>
                    💰 {t('gamification.recent_xp_earned', language)}
                  </Text>
                  {recentTransactions.slice(0, 3).map((transaction, index) => (
                    <View key={transaction.id} style={[
                      tw`flex-row items-center justify-between py-3`,
                        index < 2 && { borderBottomWidth: 1, borderBottomColor: shellColors.border }
                    ]}>
                      <View style={tw`flex-1`}>
                        <Text style={[tw`font-medium`, { color: shellColors.ivory }]}>
                          {getTranslatedDescription(transaction.description || transaction.source)}
                        </Text>
                        <Text style={[tw`text-xs`, { color: shellColors.muted }]}>
                          {new Date(transaction.created_at).toLocaleDateString()}
                        </Text>
                      </View>
                      <View style={{
                        backgroundColor: '#DCFCE7',
                        paddingHorizontal: 8,
                        paddingVertical: 4,
                        borderRadius: 8,
                      }}>
                        <Text style={[tw`font-bold text-sm`, { color: '#16A34A' }]}>
                          +{formatXP(transaction.amount)} {t('gamification.xp_short', language)}
                        </Text>
                      </View>
                    </View>
                  ))}
                </View>
              )}

              {/* Recent Achievements */}
              {recentAchievements.length > 0 && (
                <View>
                  <Text style={[tw`text-sm font-semibold mb-3`, { color: shellColors.muted }]}>
                    🏆 {t('gamification.recent_achievements_earned', language)}
                  </Text>
                  {recentAchievements.map((achievement, index) => (
                    <View key={achievement.id} style={[
                      tw`flex-row items-center py-3`,
                        index < recentAchievements.length - 1 && { borderBottomWidth: 1, borderBottomColor: shellColors.border }
                    ]}>
                      <View style={{
                        backgroundColor: '#F59E0B',
                        borderRadius: 12,
                        padding: 8,
                        marginRight: 12,
                        shadowColor: '#F59E0B',
                        shadowOffset: { width: 0, height: 2 },
                        shadowOpacity: 0.2,
                        shadowRadius: 4,
                        elevation: 3,
                      }}>
                        <Text style={{ fontSize: 20, color: 'white' }}>
                          {achievement.achievement?.icon || '🎯'}
                        </Text>
                      </View>
                      <View style={tw`flex-1`}>
                        <Text style={[tw`font-medium`, { color: shellColors.ivory }]}>
                          {achievementText(achievement.achievement?.achievement_key, 'name', achievement.achievement?.name ?? '', language)}
                        </Text>
                        <Text style={[tw`text-xs`, { color: shellColors.muted }]}>
                          {achievement.completed_at ? new Date(achievement.completed_at).toLocaleDateString() : ''}
                        </Text>
                      </View>
                      <View style={{
                        backgroundColor: '#EDE9FE',
                        paddingHorizontal: 8,
                        paddingVertical: 4,
                        borderRadius: 8,
                      }}>
                        <Text style={[tw`font-bold text-sm`, { color: '#8B5CF6' }]}>
                          +{achievement.achievement?.xp_reward || 0} {t('gamification.xp_short', language)}
                        </Text>
                      </View>
                    </View>
                  ))}
                </View>
              )}
            </View>
          )}

          <View style={tw`h-6`} />
        </ScrollView>
    </SafeAreaView>
  );
};

export default GamificationDashboard;
